import React from 'react';
import { Box, Typography, makeStyles } from '@material-ui/core';
import CodeMirror from '@uiw/react-codemirror';
import { markdown as markdownLang } from '@codemirror/lang-markdown';
import { EditorView } from '@codemirror/view';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const cmExtensions = [markdownLang(), EditorView.lineWrapping];

const useStyles = makeStyles(theme => ({
  container: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    overflow: 'hidden',
  },
  editorPane: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    borderRight: '1px solid #e0e0e0',
  },
  previewPane: {
    flex: 1,
    minWidth: 0,
    overflowY: 'auto',
    padding: theme.spacing(2),
    '&::-webkit-scrollbar': { width: 4 },
    '&::-webkit-scrollbar-thumb': { background: theme.palette.grey[300], borderRadius: 2 },
  },
  paneLabel: {
    fontSize: '0.68rem',
    fontWeight: 700,
    textTransform: 'uppercase' as any,
    letterSpacing: '0.06em',
    color: '#94a3b8',
    padding: theme.spacing(0.75, 1.5),
    borderBottom: '1px solid #e0e0e0',
    backgroundColor: '#fafafa',
    flexShrink: 0,
  },
  editorWrapper: {
    flex: 1,
    overflow: 'auto',
    '& .cm-editor': { height: '100%', fontSize: '0.82rem' },
    '& .cm-editor.cm-focused': { outline: 'none' },
    '& .cm-scroller': { fontFamily: '"JetBrains Mono", "Fira Code", monospace', lineHeight: 1.7 },
    '& .cm-content': { padding: '8px 0' },
  },
  markdownBox: {
    fontSize: '0.875rem',
    lineHeight: 1.7,
    overflowWrap: 'break-word',
    wordBreak: 'break-word',
    '& h1, & h2, & h3, & h4': { fontWeight: 700, margin: '12px 0 6px' },
    '& p': { margin: '4px 0' },
    '& ul, & ol': { paddingLeft: 20, margin: '4px 0' },
    '& li': { marginBottom: 2 },
    '& strong': { fontWeight: 700 },
    '& table': { borderCollapse: 'collapse', width: '100%', margin: '8px 0', fontSize: '0.82rem' },
    '& th, & td': { border: `1px solid ${theme.palette.grey[300]}`, padding: theme.spacing(0.75, 1.5), textAlign: 'left' },
    '& th': { backgroundColor: theme.palette.grey[100], fontWeight: 700 },
    '& code': { backgroundColor: '#f1f5f9', padding: '1px 5px', borderRadius: 4, fontSize: '0.82rem', fontFamily: 'monospace' },
    '& pre': { backgroundColor: '#f1f5f9', padding: theme.spacing(1.5), borderRadius: 6, overflowX: 'auto' },
  },
}));

interface MarkdownEditorProps {
  value: string;
  onChange: (value: string) => void;
}

export const MarkdownEditor = ({ value, onChange }: MarkdownEditorProps) => {
  const classes = useStyles();
  return (
    <Box className={classes.container}>
      <Box className={classes.editorPane}>
        <Typography className={classes.paneLabel}>Editor</Typography>
        <Box className={classes.editorWrapper}>
          <CodeMirror
            value={value}
            extensions={cmExtensions}
            onChange={onChange}
            height="100%"
            basicSetup={{ lineNumbers: true, highlightActiveLine: true, autocompletion: false, foldGutter: false }}
            style={{ height: '100%' }}
          />
        </Box>
      </Box>
      <Box className={classes.previewPane}>
        <Typography className={classes.paneLabel}>Preview</Typography>
        <Box className={classes.markdownBox} style={{ padding: '8px 4px' }}>
          {value.trim() ? (
            <ReactMarkdown remarkPlugins={[remarkGfm as any]}>{value}</ReactMarkdown>
          ) : (
            <Typography variant="body2" style={{ color: '#94a3b8', fontStyle: 'italic' }}>Nothing to preview yet…</Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};
